import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PanelLeftClose, PanelLeft, Settings, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NAV_ITEMS } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import { LogoMark } from '../ui/Logo';
import Avatar from '../ui/Avatar';

/**
 * Desktop sidebar. Collapses to a slim icon rail (76px) and expands on hover
 * as an overlay — so collapsed mode reclaims the horizontal space without
 * pushing the page content. The collapsed state is remembered.
 */
export default function Sidebar() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('aether:sidebar') === 'collapsed'
  );
  const [hovered, setHovered] = useState(false);

  // Show full content when pinned open OR temporarily hovered while collapsed.
  const expanded = !collapsed || hovered;
  const flyout = collapsed && hovered; // overlaying the content right now

  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem('aether:sidebar', !c ? 'collapsed' : 'expanded');
      return !c;
    });
  };

  return (
    // The slot reserves layout width (rail when collapsed); the panel is fixed
    // so expanding on hover floats over the content instead of shifting it.
    <div className="relative hidden shrink-0 md:block" style={{ width: collapsed ? 76 : 248 }}>
      <motion.aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        initial={false}
        animate={{ width: expanded ? 248 : 76 }}
        transition={{ type: 'spring', stiffness: 360, damping: 32 }}
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-border/60 backdrop-blur-xl transition-shadow',
          flyout ? 'bg-base/95 shadow-soft' : 'bg-surface/40'
        )}
      >
        {/* Brand + collapse */}
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/app" className="flex items-center gap-2 overflow-hidden">
            <LogoMark className="h-7 w-7 shrink-0" />
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-display text-lg font-semibold tracking-tight"
              >
                Aether
              </motion.span>
            )}
          </Link>
          {expanded && (
            <button
              onClick={toggle}
              className="rounded-lg p-1.5 text-content-subtle transition-colors hover:bg-white/5 hover:text-content"
              aria-label={collapsed ? 'Pin sidebar open' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  !expanded && 'justify-center',
                  isActive
                    ? 'bg-brand-500/15 text-content'
                    : 'text-content-muted hover:bg-white/5 hover:text-content'
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="sidebar-active"
                      className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-brand-400"
                    />
                  )}
                  <item.icon className="h-5 w-5 shrink-0" />
                  {expanded && <span className="truncate">{item.label}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Upgrade nudge */}
        {expanded && user?.plan === 'FREE' && (
          <div className="mx-3 mb-3">
            <Link
              to="/app/pricing"
              className="block rounded-xl border border-brand-500/30 bg-brand-500/10 p-3 transition-colors hover:bg-brand-500/15"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-brand-200">
                <Sparkles className="h-4 w-4" /> Upgrade to Pro
              </div>
              <p className="mt-1 text-xs text-content-muted">More generations, voice & export.</p>
            </Link>
          </div>
        )}

        {/* User */}
        <div className="border-t border-border/60 p-3">
          <Link
            to="/app/profile"
            title="Profile"
            className={cn(
              'flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-white/5',
              !expanded && 'justify-center'
            )}
          >
            <Avatar src={user?.avatarUrl} name={user?.name} size={34} />
            {expanded && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{user?.name}</p>
                <p className="truncate text-xs text-content-subtle">{user?.plan} plan</p>
              </div>
            )}
            {expanded && <Settings className="h-4 w-4 text-content-subtle" />}
          </Link>
        </div>
      </motion.aside>
    </div>
  );
}
